"use client";

import { motion } from "framer-motion";
import {
  CheckCircle,
  Smartphone,
  Star,
  Users,
  Bell,
  Shield,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Footer from "./Footer";
import ERPLeadBot from "./ERPLeadBot";


const MobileApp = () => {
  const features = [
    "Real-time notifications and updates",
    "Access to grades, attendance, and schedules",
    "Secure communication between all stakeholders",
    "Offline access to essential features",
    "Push notifications for important updates",
    "Multi-language support",
  ];

  const stats = [
    { number: "4.8", label: "App Store Rating" },
    { number: "10K+", label: "Downloads" },
    { number: "99.9%", label: "Uptime" },
    { number: "24/7", label: "Support" },
  ];

  const animationVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 },
    },
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 },
    },
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <>
    <section
      id="mobile"
      className="py-16 sm:py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white relative overflow-hidden"
    >
      {/* Abstract shapes */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
        <div className="absolute top-10 left-10 w-80 h-80 rounded-full bg-white"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-white"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-white"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center">
          <motion.div
            className="lg:w-1/2 mb-10 lg:mb-0"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={slideInLeft}
          >
            <div className="relative">
              <motion.div
                className="relative z-10 bg-white p-2 rounded-3xl shadow-2xl mx-auto max-w-xl"
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  repeat: Number.POSITIVE_INFINITY,
                  duration: 3,
                  ease: "easeInOut",
                }}
              >
                <div className="rounded-2xl overflow-hidden">
                  <img src="/Mobileimages.png" alt="Clynox mobile app for school management" width="1080" height="1350" loading="lazy" decoding="async" className="w-full h-auto" />
                </div>
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-16 h-1 bg-gray-300 rounded-full"></div>
              </motion.div>

              {/* Phone frame shadow */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-96 bg-black/20 rounded-[40px] blur-xl -z-10"></div>
            </div>
          </motion.div>

          <motion.div
            className="lg:w-1/2 lg:pl-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={slideInRight}
          >
            <Badge className="mb-4 bg-white/20 text-white hover:bg-white/30 uppercase tracking-wider">
              MOBILE ACCESS
            </Badge>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
              ONE APP FOR ALL USERS
            </h2>
            
            <p className="text-lg sm:text-xl mb-8 text-blue-50">
              Experience seamless education management on the go with our powerful mobile application, available for
              Android and iOS devices.
            </p>

            <motion.div
              className="space-y-4 mb-8"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex items-start bg-white/10 p-4 rounded-xl backdrop-blur-sm"
                  variants={animationVariants}
                  whileHover={{ x: 5, backgroundColor: "rgba(255, 255, 255, 0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <CheckCircle className="h-6 w-6 text-blue-200 mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-base sm:text-lg">{feature}</p>
                </motion.div>
              ))}
            </motion.div>


            {/* App Store Badges */}
            <motion.div
              className="flex flex-col sm:flex-row gap-2 sm:gap-3"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div variants={scaleIn} className="flex-1">
                <a
                  href="https://play.google.com/store/apps/details?id=com.application.clynox"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Get it on Google Play"
                    className="h-16 w-auto mx-auto hover:scale-105 transition-transform duration-300"
                  />
                </a>
              </motion.div>
              
              <motion.div variants={scaleIn} className="flex-1">
                <a
                  href="https://apps.apple.com/in/app/clynox/id6753124884"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                    alt="Download on the App Store"
                    className="h-16 w-auto mx-auto hover:scale-105 transition-transform duration-300"
                  />
                </a>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        {/* Stats Section */}
        <motion.div
          className="mt-16 sm:mt-20"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                className="text-center p-4 sm:p-6 bg-white/10 rounded-xl backdrop-blur-sm hover:bg-white/20 transition-all duration-300"
              >
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-sm sm:text-base text-blue-100">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
    <Footer />
    <ERPLeadBot />
    </>
  );
};

export default MobileApp;
